import { SerializedConstructor } from '@langchain/core/load/serializable';

import { useMemo } from 'react';

import { Manifest } from '@/Pages/Playground/components/manifest/Manifest';
import { EvaluatedEntityType } from '@/components/EvaluatorCrudPane/utils/serializeFeedbackUIToLC';
import { FiltersDisplay } from '@/components/FiltersDisplay/FiltersDisplay';
import { Skeleton } from '@/components/Skeleton';
import { useIsPromptLoading } from '@/hooks/useIsPromptLoading';
import { MessageCapability } from '@/types/schema';

import { useParsedManifest } from '../../../components/EvaluatorCrudPane/utils/useParsedManifest';
import {
  HubPromptDisplay,
  ModelDisplay,
  SamplingRateDisplay,
} from './ModelAndRuleDisplayComponents';

export const PromptSection = ({
  evaluatedEntityType,
  model,
  variableMapping,
  filter,
  traceFilter,
  treeFilter,
  samplingRate,
  isEvaluatorDisabled,
}: {
  evaluatedEntityType: EvaluatedEntityType;
  model: SerializedConstructor | null;
  variableMapping: Record<string, string> | null;
  filter?: string;
  traceFilter?: string;
  treeFilter?: string;
  samplingRate?: number;
  isEvaluatorDisabled?: boolean;
}) => {
  const { manifest, disabledMessageTypes } =
    useParsedManifest(evaluatedEntityType);

  // Show loading state if hub manifest is loading or if store hasn't been populated yet
  const isLoading = useIsPromptLoading(evaluatedEntityType);

  const variableMappingProps = useMemo(
    () => ({
      storeType: 'detail' as const,
      variableMappings: variableMapping,
      availablePaths: null,
      uneditableVariables: null,
      setVariableMappings: () => void 0,
      setActiveVariable: () => void 0,
    }),
    [variableMapping]
  );

  if (isLoading) {
    return (
      <div className="flex flex-col">
        <Skeleton className="h-[100px] w-full rounded-lg" />
        <Skeleton className="-mt-0.5 h-[100px] w-full rounded-lg" />
      </div>
    );
  }

  if (!manifest?.kwargs?.first) {
    return (
      <div className="text-tertiary">No prompt configuration available</div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {isEvaluatorDisabled && (
        <div className="text-warning-primary rounded-lg border border-warning bg-warning-primary p-3 text-sm">
          To enable this evaluator, please update your prompt from the default.
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <HubPromptDisplay />
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <ModelDisplay model={model} />
          {(filter != null || traceFilter != null || treeFilter != null) && (
            <FiltersDisplay
              filter={filter}
              traceFilter={traceFilter}
              treeFilter={treeFilter}
            />
          )}
          {samplingRate != null && (
            <SamplingRateDisplay samplingRate={samplingRate} />
          )}
        </div>
      </div>
      <Manifest
        variant="evaluator"
        value={manifest.kwargs.first}
        onChange={() => {
          // Read-only, no changes allowed
        }}
        hideTemplateFormatSelector
        outputSchemaAsModal
        minimizedSchema={true}
        disabledMessageTypes={disabledMessageTypes}
        disabledMessageCapabilities={[
          MessageCapability.IMAGE,
          MessageCapability.CANVAS,
        ]}
        isAllCollapsed={false}
        readOnly={true}
        options={{}}
        onOptionsChange={() => void 0}
        variableMappingProps={variableMappingProps}
      />
    </div>
  );
};
